import { AnimatePresence, m } from 'motion/react';

import { StarIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { Pin, usePins } from 'utils/pins';

const GONE = { opacity: 0, scale: 0.25, filter: 'blur(2px)' };
const THERE = { opacity: 1, scale: 1, filter: 'blur(0px)' };
// Icon swap: 250ms (`--duration-fast`) on ease-in-out.
const SWAP = { duration: 0.25, ease: 'easeInOut' } as const;

/**
 * The star pinning a record to the sidebar's Pinned section, or taking it off: amber-brown and filled while pinned.
 * The outline and the filled star cross-fade as it toggles, with opacity alone under reduced motion, and not as the
 * button first shows.
 */
export default function PinButton({ pin }: { pin: Pin }) {
    const { isPinned, togglePin } = usePins();
    const still = useReducedMotion();
    const pinned = isPinned(pin.key);
    const label = t(pinned ? 'pins.unpin' : 'pins.pin');

    return (
        <Tooltip content={label}>
            <Button
                variant="ghost"
                size="icon-sm"
                className={cn('relative size-7 rounded-md', pinned ? 'text-warning-ink' : 'text-muted-foreground')}
                aria-label={label}
                aria-pressed={pinned}
                onClick={() => togglePin(pin)}
            >
                <AnimatePresence initial={false} mode="popLayout">
                    <m.span
                        key={pinned ? 'pinned' : 'unpinned'}
                        className="flex"
                        initial={still ? { opacity: 0 } : GONE}
                        animate={still ? { opacity: 1 } : THERE}
                        exit={still ? { opacity: 0 } : GONE}
                        transition={still ? { duration: 0.15, ease: 'easeOut' } : SWAP}
                    >
                        <StarIcon fill={pinned ? 'currentColor' : 'none'} className="size-4" />
                    </m.span>
                </AnimatePresence>
            </Button>
        </Tooltip>
    );
}
